
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Alert, Form } from "react-bootstrap";
import { IconCaretLeft } from "@tabler/icons-react";
import { Helmet } from "react-helmet-async";

const normalizeURL = (url) => {
  try {
    const parsedUrl = new URL(url.trim());
    const protocol = parsedUrl.protocol;
    const hostname = parsedUrl.hostname.toLowerCase(); // domain to lowercase
    const pathname = parsedUrl.pathname;               // keep path as is
    const search = parsedUrl.search;                   // keep query string
    return `${protocol}//${hostname}${pathname}${search}`;
  } catch {
    return null;
  }
};

const AddLinks = () => {
  const [file, setFile] = useState(null);
  const [urlList, setUrlList] = useState("");
  const [userCredits, setUserCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null); // can be string or {text, link:true}
  const [successMessage, setSuccessMessage] = useState("");

  // fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch("/api/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUserCredits(data.credits || 0);
        }
      } catch (err) {
        console.error("Error fetching balance:", err);
      }
    };
    fetchCredits();
  }, []);

  // pre-count unique links to show Credits used X/Y
  useEffect(() => {
    const countUniqueLinks = async () => {
      try {
        let linkArray = urlList
          .split(/\r?\n/)
          .map((link) => normalizeURL(link))
          .filter((link) => link !== null);

        if (file) {
          const fileContent = await file.text();
          const fileLinks = fileContent
            .split(/\r?\n/)
            .map((link) => normalizeURL(link))
            .filter((link) => link !== null);
          linkArray = [...linkArray, ...fileLinks];
        }

        const uniqueLinks = [...new Set(linkArray)];
        setCreditsUsed(uniqueLinks.length);
      } catch (e) {
        console.error("Error counting links:", e);
      }
    };

    countUniqueLinks();
  }, [urlList, file]);

  // validate and deduplicate
  const validateLinks = async () => {
    let linkArray = urlList
      .split(/\r?\n/)
      .map((link) => normalizeURL(link))
      .filter((link) => link !== null);

    if (file) {
      try {
        const fileContent = await file.text();
        const fileLinks = fileContent
          .split(/\r?\n/)
          .map((link) => normalizeURL(link))
          .filter((link) => link !== null);

        linkArray = [...new Set([...linkArray, ...fileLinks])];
      } catch (error) {
        setError("File reading error.");
        return null;
      }
    }

    const uniqueSet = new Set(linkArray);
    setCreditsUsed(uniqueSet.size);

    if (uniqueSet.size > userCredits) {
      setError({
        text: `Not enough credits. Required: ${uniqueSet.size}, you have: ${userCredits}.`,
        link: true,
      });
      return null;
    }

    linkArray = [...new Set(linkArray)];

    if (linkArray.length > 2000) {
      setError(`Limit exceeded: maximum 2000 links. You entered ${linkArray.length}.`);
      return null;
    }

    return linkArray;
  };

  // add links
  const handleAddLinks = async () => {
    setError(null);
    setSuccessMessage("");
    setIsSubmitting(true);

    const uniqueLinks = await validateLinks();
    if (!uniqueLinks) {
      setIsSubmitting(false);
      return;
    }

    if (uniqueLinks.length === 0) {
      setError("Enter at least one valid link.");
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`/api/projects/${id}/urls`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ urls: uniqueLinks }),
      });

      if (response.ok) {
        navigate(`/project/${id}`);
        return;
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.detail || "❌ Error adding links.");
      }

      setUrlList("");
      setFile(null);
    } catch (error) {
      console.error("❌ Error in `handleAddLinks()`:", error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
  <>
       
            <Helmet> <title>Add Links - IndexBooster</title></Helmet>
    <Card>
      <Card.Header className="d-flex align-items-center gap-2">
        <Button variant="light" size="sm" onClick={() => navigate(-1)}>
          <IconCaretLeft size={18} />
        </Button>
        <h3 className="card-title mb-0">Add Links</h3>
      </Card.Header>

      <Card.Body>
        {error && (
          <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>
            {typeof error === "string" ? (
              error
            ) : (
              <>
                {error.text} <a href="/buy-credits">Top up balance</a>
              </>
            )}
          </Alert>
        )}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        <Form>
          <Form.Group>
            <Form.Label>Enter links (one per line):</Form.Label>
            <Form.Text className={creditsUsed > userCredits ? "text-danger fw-bold" : "text-muted"}>
              Credits used: {creditsUsed}/{userCredits}
            </Form.Text>

            <Form.Control
              as="textarea"
              value={urlList}
              onChange={(e) => setUrlList(e.target.value)}
              rows="12"
              placeholder={`https://example.com\nhttps://example2.com\nhttps://example3.com\nhttps://example4.com\nhttps://example5.com\nhttps://example6.com\nhttps://example7.com\nhttps://example8.com\nhttps://example9.com\nhttps://example10.com\n...`}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Import file (CSV/TXT, max 1MB):</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".csv,.txt"
            />
          </Form.Group>
        </Form>
      </Card.Body>

      <Card.Footer className="d-flex justify-content-between">
        <Button variant="success" onClick={handleAddLinks} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Adding Links...
            </>
          ) : (
            "Add Links"
          )}
        </Button>
      </Card.Footer>
    </Card>
  </>);
};

export default AddLinks;
